import { useState } from 'react';
import { X, Building2, CreditCard, Loader2 } from 'lucide-react';
import { useSubscriptionPlans } from '@/hooks/useClients';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormData {
  name: string;
  slug: string;
  contactEmail: string;
  contactPhone: string;
  planName: string;
  licensedLocations: number;
  licensedRegisters: number;
  billingExempt: boolean;
  billingInterval: 'monthly' | 'annual';
  status: 'trialing' | 'active';
  trialDays: number;
}

const initialFormData: FormData = {
  name: '',
  slug: '',
  contactEmail: '',
  contactPhone: '',
  planName: '',
  licensedLocations: 1,
  licensedRegisters: 2,
  billingExempt: false,
  billingInterval: 'monthly',
  status: 'trialing',
  trialDays: 14,
};

export function AddClientModal({ isOpen, onClose, onSuccess }: AddClientModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: plans, isLoading: plansLoading } = useSubscriptionPlans();
  const queryClient = useQueryClient();

  const handleInputChange = (field: keyof FormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);

    if (field === 'name') {
      const slug = (value as string)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  };

  const validateStep1 = () => {
    if (!formData.name.trim()) return 'Business name is required';
    if (!formData.slug.trim()) return 'URL slug is required';
    if (!/^[a-z0-9-]+$/.test(formData.slug)) return 'Slug can only contain lowercase letters, numbers, and hyphens';
    return null;
  };

  const validateStep2 = () => {
    if (!formData.planName) return 'Please select a plan';
    if (formData.licensedLocations < 1) return 'At least 1 location is required';
    if (formData.licensedRegisters < 1) return 'At least 1 register is required';
    return null;
  };

  const handleNext = () => {
    const validationError = validateStep1();
    if (validationError) {
      setError(validationError);
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    setError(null);
    setStep(1);
  };

  const handleSubmit = async () => {
    const validationError = validateStep2();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Create organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: formData.name.trim(),
          slug: formData.slug.trim(),
          subscription_plan: formData.planName,
          subscription_status: formData.status,
          contact_email: formData.contactEmail || null,
          contact_phone: formData.contactPhone || null,
        } as never)
        .select()
        .single();

      if (orgError) throw orgError;

      const orgId = (orgData as any).id;

      // Calculate trial end date
      const trialEndsAt = formData.status === 'trialing'
        ? new Date(Date.now() + formData.trialDays * 86400000).toISOString()
        : null;

      // Create subscription_info
      const { error: subError } = await supabase
        .from('subscription_info')
        .insert({
          organization_id: orgId,
          plan_name: formData.planName,
          licensed_locations: formData.licensedLocations,
          licensed_registers: formData.licensedRegisters,
          status: formData.status,
          billing_exempt: formData.billingExempt,
          billing_interval: formData.billingInterval,
          trial_ends_at: trialEndsAt,
          current_period_end: trialEndsAt || new Date(Date.now() + 30 * 86400000).toISOString(),
        } as never);

      if (subError) {
        // Rollback: delete the organization
        await supabase.from('organizations').delete().eq('id', orgId as never);
        throw subError;
      }

      // Log the event
      await supabase.from('billing_events').insert({
        organization_id: orgId,
        event_type: 'client_created',
        resource_type: 'organization',
        new_value: {
          name: formData.name,
          plan: formData.planName,
          status: formData.status,
        },
        notes: 'Client created from admin console',
      } as never);

      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });

      setFormData(initialFormData);
      setStep(1);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setStep(1);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Add New Client</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Step {step} of 2 - {step === 1 ? 'Business Info' : 'Subscription'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-gray-100 dark:bg-gray-700">
          <div
            className="h-full bg-blue-600 transition-all duration-300"
            style={{ width: `${(step / 2) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Business Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Business Information</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Basic details about the client</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Joe's Coffee Shop"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  URL Slug *
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value.toLowerCase())}
                    placeholder="joes-coffee"
                    className="flex-1 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-l-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-l-0 border-gray-200 dark:border-gray-700 rounded-r-lg text-sm text-gray-500 dark:text-gray-400">
                    .blazepos.app
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                    placeholder="joe@example.com"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                    placeholder="(555) 123-4567"
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Subscription */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">Subscription Setup</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Plan and billing configuration</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select Plan *
                </label>
                {plansLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {plans?.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        onClick={() => handleInputChange('planName', plan.name)}
                        className={cn(
                          'p-4 border rounded-lg text-left transition-all',
                          formData.planName === plan.name
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        )}
                      >
                        <p className="font-medium text-gray-900 dark:text-white">{plan.name}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                          ${plan.base_price}<span className="text-sm font-normal text-gray-500">/mo</span>
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {plan.unlimited_locations ? 'Unlimited' : plan.included_locations} loc, {plan.unlimited_registers ? 'Unlimited' : plan.included_registers} reg
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Licensed Locations
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.licensedLocations}
                    onChange={(e) => handleInputChange('licensedLocations', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Licensed Registers
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.licensedRegisters}
                    onChange={(e) => handleInputChange('licensedRegisters', parseInt(e.target.value) || 1)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Initial Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="trialing">Trial</option>
                    <option value="active">Active</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Interval
                  </label>
                  <select
                    value={formData.billingInterval}
                    onChange={(e) => handleInputChange('billingInterval', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="annual">Annual (20% off)</option>
                  </select>
                </div>
                {formData.status === 'trialing' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Trial Days
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={formData.trialDays}
                      onChange={(e) => handleInputChange('trialDays', parseInt(e.target.value) || 14)}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <input
                  type="checkbox"
                  id="billingExempt"
                  checked={formData.billingExempt}
                  onChange={(e) => handleInputChange('billingExempt', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="billingExempt" className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Billing Exempt</span> - Skip Stripe billing (for demos, development, comped accounts)
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <button
            onClick={step === 1 ? handleClose : handleBack}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < 2 ? (
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Client
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
